import { getDefaultFixtureForComponent } from '@web-studio/core-fixtures';
import {
  PACKAGE_TIER_KEY_VALUES,
  PLATFORM_VERTICAL_KEY_VALUES,
  REGISTRY_BLOCK,
  THEME_ID,
  type PackageTierKey,
  type PlatformVerticalKey,
} from '@web-studio/shared-schemas/catalog';
import type { LayoutBlock, LayoutComponentType } from '@web-studio/shared-schemas/content';
import {
  COMPONENT_TYPE_REGISTRY_KEYS,
  getAllowedBlocksForTier,
  PLATFORM_VERTICAL_MATRIX,
  resolveComponentTypeForRegistryBlock,
  resolvePresentationForBlockType,
} from '@web-studio/shared-schemas/matrix';
import { IMPLEMENTED_COMPONENT_TYPES } from './blockCatalog';

export interface TierBlockRow {
  registryKey: string;
  componentType: LayoutComponentType | null;
  tierAccess: 'active' | 'frozen' | 'registry_only';
  hasFixture: boolean;
}

export interface TierPreviewResult {
  vertical: PlatformVerticalKey;
  tier: PackageTierKey;
  allowedKeys: string[];
  rows: TierBlockRow[];
  previewBlocks: LayoutBlock[];
  defaultThemeId: string;
}

const T = REGISTRY_BLOCK.type;

const DISPLAY_ORDER: Record<string, number> = {
  [T.navigation]: 0,
  [T.hero]: 1,
  [T.staticMenu]: 2,
  [T.dynamicMenu]: 3,
  [T.reservation]: 4,
  [T.chefStory]: 5,
  [T.tastingTimeline]: 6,
  [T.allergyForm]: 7,
  [T.footer]: 99,
};

export function listTierBlockRows(
  vertical: PlatformVerticalKey,
  tier: PackageTierKey,
  themeId?: string,
): TierBlockRow[] {
  const allowed = getAllowedBlocksForTier(vertical, tier);

  return allowed.map((blockType) => {
    const presentation = resolvePresentationForBlockType(blockType, themeId);
    const componentType = resolveComponentTypeForRegistryBlock(presentation) ?? null;
    const implemented =
      componentType !== null &&
      (IMPLEMENTED_COMPONENT_TYPES as readonly string[]).includes(componentType);

    if (!implemented) {
      return {
        registryKey: blockType,
        componentType: null,
        tierAccess: 'registry_only',
        hasFixture: false,
      };
    }

    const fixture = getDefaultFixtureForComponent(componentType);
    return {
      registryKey: blockType,
      componentType,
      tierAccess: 'active',
      hasFixture: Boolean(fixture),
    };
  });
}

export function buildTierPreview(
  vertical: PlatformVerticalKey,
  tier: PackageTierKey,
): TierPreviewResult {
  const definition = PLATFORM_VERTICAL_MATRIX[vertical];
  const themeId = definition?.defaultTheme ?? THEME_ID.michelinDark;
  const rows = listTierBlockRows(vertical, tier, themeId);
  const previewBlocks: LayoutBlock[] = [];

  for (const row of rows) {
    if (row.tierAccess !== 'active' || !row.componentType || !row.hasFixture) {
      continue;
    }
    const fixture = getDefaultFixtureForComponent(row.componentType);
    if (!fixture) continue;
    const order =
      DISPLAY_ORDER[row.componentType] ??
      DISPLAY_ORDER[row.registryKey] ??
      previewBlocks.length + 1;
    previewBlocks.push({
      ...fixture.block,
      displayOrder: order,
    });
  }

  previewBlocks.sort((a, b) => a.displayOrder - b.displayOrder);

  return {
    vertical,
    tier,
    allowedKeys: rows.map((r) => r.registryKey),
    rows,
    previewBlocks,
    defaultThemeId: themeId,
  };
}

export function listVerticalTierPairs(): { vertical: PlatformVerticalKey; tier: PackageTierKey }[] {
  const pairs: { vertical: PlatformVerticalKey; tier: PackageTierKey }[] = [];
  for (const vertical of PLATFORM_VERTICAL_KEY_VALUES) {
    for (const tier of PACKAGE_TIER_KEY_VALUES) {
      pairs.push({ vertical, tier });
    }
  }
  return pairs;
}

export function registryKeyForComponent(componentType: LayoutComponentType): string {
  return COMPONENT_TYPE_REGISTRY_KEYS[componentType] ?? componentType;
}
